import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../hooks/useAuth";
import SEO from "../components/SEO";
import { trackEvent } from "../lib/analytics";

type AddressFields = { street: string; city: string; state: string; zip: string };

const EMPTY_ADDR: AddressFields = { street: "", city: "", state: "", zip: "" };

function serializeAddress(a: AddressFields): string {
  if (!a.street && !a.city && !a.state && !a.zip) return "";
  return JSON.stringify({
    street: a.street.trim(),
    city:   a.city.trim(),
    state:  a.state.trim(),
    zip:    a.zip.trim(),
  });
}

function AddressBlock({
  label,
  sublabel,
  value,
  onChange,
}: {
  label: string;
  sublabel?: string;
  value: AddressFields;
  onChange: (v: AddressFields) => void;
}) {
  return (
    <div className="space-y-2">
      <div>
        <label className="block text-sm font-medium text-primary mb-0.5">{label}</label>
        {sublabel && <p className="text-xs text-gray-500 mb-1">{sublabel}</p>}
      </div>
      <input
        type="text"
        className="input-field"
        placeholder="Street Address"
        value={value.street}
        onChange={e => onChange({ ...value, street: e.target.value })}
      />
      <div className="grid grid-cols-5 gap-2">
        <input
          type="text"
          className="input-field col-span-2"
          placeholder="City"
          value={value.city}
          onChange={e => onChange({ ...value, city: e.target.value })}
        />
        <input
          type="text"
          className="input-field col-span-1 uppercase"
          placeholder="State"
          maxLength={2}
          value={value.state}
          onChange={e => onChange({ ...value, state: e.target.value.toUpperCase() })}
        />
        <input
          type="text"
          className="input-field col-span-2"
          placeholder="ZIP Code"
          maxLength={10}
          value={value.zip}
          onChange={e => onChange({ ...value, zip: e.target.value })}
        />
      </div>
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<boolean>(false);
  const [countdown, setCountdown] = useState(5);
  const [form, setForm] = useState({
    email: "", password: "", confirmPassword: "",
    firstName: "", lastName: "", companyName: "", phone: "",
    npiNumber: "", deaNumber: "", stateLicense: "",
    deaExpiration: "", stateLicenseExpiration: "",
  });
  const [billing, setBilling] = useState<AddressFields>(EMPTY_ADDR);
  const [shipping, setShipping] = useState<AddressFields>(EMPTY_ADDR);
  const [sameAsBilling, setSameAsBilling] = useState(false);

  useEffect(() => {
    if (step !== 3 || !registrationResult) return;
    if (countdown <= 0) {
      navigate("/login");
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, registrationResult, countdown, navigate]);

  const update = (field: string, value: string) => setForm({ ...form, [field]: value });

  const toggleSameAsBilling = (checked: boolean) => {
    setSameAsBilling(checked);
    if (checked) setShipping({ ...billing });
  };

  const handleBillingChange = (v: AddressFields) => {
    setBilling(v);
    if (sameAsBilling) setShipping({ ...v });
  };

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (step === 1) {
      if (!form.firstName || !form.lastName || !form.email || !form.companyName) {
        setError("Please fill in all required fields");
        return;
      }
      if (form.password.length < 8) {
        setError("Password must be at least 8 characters");
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
    }
    setError("");
    if (step === 1) {
      fetch("/api/auth/step1-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          companyName: form.companyName,
          email: form.email,
          phone: form.phone,
          billingAddress: serializeAddress(billing),
          password: form.password,
        }),
      }).then(res => {
        if (!res.ok) {
          res.json().then(data => {
            toast.error(data?.error || "Something went wrong saving your info. You can continue to the next step.");
          }).catch(() => {
            toast.error("Something went wrong saving your info. You can continue to the next step.");
          });
        }
      }).catch(() => {
        toast.error("Something went wrong. Please try again.");
      });
    }
    setStep(step + 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const effectiveShipping = sameAsBilling ? billing : shipping;
    const result = await register({
      email: form.email,
      password: form.password,
      firstName: form.firstName,
      lastName: form.lastName,
      companyName: form.companyName,
      phone: form.phone,
      billingAddress: serializeAddress(billing),
      shippingAddress: serializeAddress(effectiveShipping),
      npiNumber: form.npiNumber,
      deaNumber: form.deaNumber,
      stateLicense: form.stateLicense,
      deaExpiration: form.deaExpiration,
      stateLicenseExpiration: form.stateLicenseExpiration,
    });
    setLoading(false);
    if (result.success) {
      setRegistrationResult(true);
      setStep(3);
      trackEvent("sign_up", { method: "email" });
    } else {
      setError(result.error || "Registration failed");
    }
  };

  return (
    <>
      <SEO title="Open a Wholesale Account" description="Open a Premier Pharma wholesale account. Licensed pharmacies and clinics get instant approval with valid NPI and DEA credentials." />
      <section className="min-h-[80vh] flex items-center justify-center py-12 bg-background">
        <div className="card max-w-lg w-full mx-4">
          {step <= 2 && (
            <>
              <div className="text-center mb-6">
                <img src="/logo.svg" alt="Premier Pharma" className="h-10 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-primary">Create Your Account</h1>
                <p className="text-gray-500 text-sm mt-1">Step {step} of 2</p>
                <div className="flex gap-2 mt-3 justify-center">
                  <div className={`h-1 w-16 rounded-full ${step >= 1 ? "bg-accent" : "bg-gray-200"}`}></div>
                  <div className={`h-1 w-16 rounded-full ${step >= 2 ? "bg-accent" : "bg-gray-200"}`}></div>
                </div>
              </div>

              {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-4">{error}</div>}

              <form onSubmit={handleSubmit}>
                {step === 1 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="reg-firstName" className="block text-sm font-medium text-primary mb-1">First Name *</label>
                        <input id="reg-firstName" type="text" className="input-field" value={form.firstName} onChange={e => update("firstName", e.target.value)} />
                      </div>
                      <div>
                        <label htmlFor="reg-lastName" className="block text-sm font-medium text-primary mb-1">Last Name *</label>
                        <input id="reg-lastName" type="text" className="input-field" value={form.lastName} onChange={e => update("lastName", e.target.value)} />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="reg-companyName" className="block text-sm font-medium text-primary mb-1">Company Name *</label>
                      <input id="reg-companyName" type="text" className="input-field" value={form.companyName} onChange={e => update("companyName", e.target.value)} />
                    </div>

                    <div>
                      <label htmlFor="reg-email" className="block text-sm font-medium text-primary mb-1">Email *</label>
                      <input id="reg-email" type="email" className="input-field" value={form.email} onChange={e => update("email", e.target.value)} />
                    </div>

                    <div>
                      <label htmlFor="reg-phone" className="block text-sm font-medium text-primary mb-1">Phone</label>
                      <input id="reg-phone" type="tel" className="input-field" value={form.phone} onChange={e => update("phone", e.target.value)} />
                    </div>

                    {/* Billing Address */}
                    <div className="pt-1 border-t border-gray-100">
                      <AddressBlock
                        label="Billing Address"
                        value={billing}
                        onChange={handleBillingChange}
                      />
                    </div>

                    {/* Shipping Address */}
                    <div className="pt-1 border-t border-gray-100">
                      <p className="text-sm font-medium text-primary mb-2">Shipping Address</p>
                      <label className="flex items-center gap-2 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-accent focus:ring-accent"
                          checked={sameAsBilling}
                          onChange={e => toggleSameAsBilling(e.target.checked)}
                        />
                        <span className="text-sm text-gray-600">Same as billing address</span>
                      </label>
                      {!sameAsBilling && (
                        <AddressBlock
                          label=""
                          sublabel="This address will be used as your default shipping address."
                          value={shipping}
                          onChange={setShipping}
                        />
                      )}
                    </div>

                    <div>
                      <label htmlFor="reg-password" className="block text-sm font-medium text-primary mb-1">Password *</label>
                      <input id="reg-password" type="password" className="input-field" value={form.password} onChange={e => update("password", e.target.value)} placeholder="Min 8 characters" />
                    </div>
                    <div>
                      <label htmlFor="reg-confirmPassword" className="block text-sm font-medium text-primary mb-1">Confirm Password *</label>
                      <input id="reg-confirmPassword" type="password" className="input-field" value={form.confirmPassword} onChange={e => update("confirmPassword", e.target.value)} />
                    </div>

                    <button type="button" onClick={handleNext} className="btn-primary w-full">Next: License Info</button>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm mb-4">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div>
                          <span className="font-semibold text-blue-800">License Verification Required</span>
                          <p className="text-blue-700 mt-1">Please provide your licensing credentials below. Our team will review and verify your information before activating your account.</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="reg-npiNumber" className="block text-sm font-medium text-primary mb-1">NPI Number</label>
                      <input id="reg-npiNumber" type="text" className="input-field" value={form.npiNumber} onChange={e => update("npiNumber", e.target.value)} placeholder="10-digit NPI number" maxLength={10} />
                      {form.npiNumber.length > 0 && !/^\d{10}$/.test(form.npiNumber) && (
                        <p className="text-red-500 text-xs mt-1">
                          {form.npiNumber.length < 10 ? "NPI must be exactly 10 digits" : "NPI must contain only digits"}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="reg-deaNumber" className="block text-sm font-medium text-primary mb-1">DEA Number</label>
                      <input id="reg-deaNumber" type="text" className="input-field" value={form.deaNumber} onChange={e => update("deaNumber", e.target.value)} placeholder="DEA registration number" maxLength={9} />
                    </div>

                    <div>
                      <label htmlFor="reg-deaExpiration" className="block text-sm font-medium text-primary mb-1">DEA Expiration Date</label>
                      <input id="reg-deaExpiration" type="date" className="input-field" value={form.deaExpiration} onChange={e => update("deaExpiration", e.target.value)} />
                    </div>

                    <div>
                      <label htmlFor="reg-stateLicense" className="block text-sm font-medium text-primary mb-1">State License Number</label>
                      <input id="reg-stateLicense" type="text" className="input-field" value={form.stateLicense} onChange={e => update("stateLicense", e.target.value)} placeholder="State pharmacy license" />
                    </div>

                    <div>
                      <label htmlFor="reg-stateLicenseExpiration" className="block text-sm font-medium text-primary mb-1">State License Expiration Date</label>
                      <input id="reg-stateLicenseExpiration" type="date" className="input-field" value={form.stateLicenseExpiration} onChange={e => update("stateLicenseExpiration", e.target.value)} />
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                      <div className="flex gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>NABP</span>
                        <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>DSCSA</span>
                        <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5"><span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>DEA</span>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button type="button" onClick={() => setStep(1)} className="btn-outline flex-1">Back</button>
                      <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-50">
                        {loading ? "Creating..." : "Create Account"}
                      </button>
                    </div>
                  </div>
                )}
              </form>

              <p className="text-center text-sm text-gray-500 mt-6">
                Already have an account? <Link to="/login" className="text-accent font-medium hover:underline">Log in</Link>
              </p>
            </>
          )}

          {step === 3 && registrationResult && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h2 className="text-2xl font-bold text-primary mb-2">Welcome, {form.firstName}! Account Created.</h2>
              <p className="text-gray-600 mb-3">Your account has been created. Our team will review and verify your credentials — you'll be notified once your account is approved.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 mb-6">
                Verification typically completes within 1–2 business days. You'll receive an email when your account is approved.
              </div>
              <div className="mt-2">
                <Link to="/login" className="btn-primary inline-block px-8 py-3 text-lg">Go to Login</Link>
                <p className="text-sm text-gray-400 mt-3">Redirecting in {countdown} second{countdown !== 1 ? "s" : ""}...</p>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
